"use client";
import React, {createContext, useContext, useState, useEffect} from "react";
import {type ThemeContextValue} from "@/types/types";

const defaultThemeContextValue: ThemeContextValue = {
  theme: "dark",
  setTheme: () => {},
};

const ThemeContext = createContext<ThemeContextValue>(defaultThemeContextValue);

function getInitialTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "dark";
  const stored = localStorage.getItem("theme") as "light" | "dark" | null;
  if (stored) return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

const ThemeProvider: React.FC<React.PropsWithChildren> = ({children}) => {
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setTheme(getInitialTheme());
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme, mounted]);

  if (!mounted) {
    return (
      <div style={{visibility: "hidden"}}>
        {children}
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={{theme, setTheme}}>
      {children}
    </ThemeContext.Provider>
  );
};

export {ThemeProvider, ThemeContext};
export const useTheme = () => useContext(ThemeContext);
